'use client';

import { Scale, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useDisputesList } from '@/lib/api/disputes';

import { DisputesTable } from './components/DisputesTable';

export default withRequiredPermission(DisputesPage, PERMISSIONS.PAYMENTS_VIEW);

function DisputesPage() {
  const { t } = useTranslation('disputes');
  const { data, isLoading, isFetching, isError, error } = useDisputesList({ limit: 200 });

  const disputes = data?.data ?? [];
  const hasNoPayfacAccount = Boolean(data?.message);
  const hasNoDisputes = !hasNoPayfacAccount && !isLoading && disputes.length === 0;

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <PageHeader title={t('title')} description={t('description')} />

      <TableCard>
        {hasNoPayfacAccount ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Wallet className="h-6 w-6" />
              </EmptyMedia>
              <EmptyTitle>{t('empty.noPayfacAccount.title')}</EmptyTitle>
              <EmptyDescription>{t('empty.noPayfacAccount.description')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : hasNoDisputes ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Scale className="h-6 w-6" />
              </EmptyMedia>
              <EmptyTitle>{t('empty.noDisputes.title')}</EmptyTitle>
              <EmptyDescription>{t('empty.noDisputes.description')}</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <>
            {data?.hasMore && (
              <div className="text-muted-foreground px-1 pb-2 text-xs">
                {t('showingLatest', { count: disputes.length })}
              </div>
            )}
            <DisputesTable disputes={disputes} isLoading={isLoading || isFetching} />
          </>
        )}

        {isError && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('loadError')}
          </div>
        )}
      </TableCard>
    </div>
  );
}
